import { STORE_MUTATION } from "../enums";
import { MessageContent } from "../types";
import { store } from "../store/store";
import { getMessageType, TimestampsUtil } from ".";

// Store the timeout id for the traffic status bar.
let trafficStatusTimeoutId: ReturnType<typeof setTimeout> | undefined;

/**
 * Show a text-only status bar above the input and auto-hide it after the timeout.
 */
function showTrafficStatusBar(text: string, timeout: number) {
  if (trafficStatusTimeoutId) {
    clearTimeout(trafficStatusTimeoutId);
  }
  store.commit(STORE_MUTATION.SetTrafficStatusText, text);
  store.commit(STORE_MUTATION.SetShowTrafficStatusBar, true);
  if (timeout > 0) {
    trafficStatusTimeoutId = setTimeout(() => {
      store.commit(STORE_MUTATION.SetShowTrafficStatusBar, false);
      store.commit(STORE_MUTATION.SetTrafficStatusText, "");
      trafficStatusTimeoutId = undefined;
    }, timeout);
  }
}

/**
 * Clears the traffic status bar immediately (called when a real message arrives).
 */
export function clearTrafficStatusBar() {
  if (trafficStatusTimeoutId) {
    clearTimeout(trafficStatusTimeoutId);
    trafficStatusTimeoutId = undefined;
  }
  store.commit(STORE_MUTATION.SetShowTrafficStatusBar, false);
  store.commit(STORE_MUTATION.SetTrafficStatusText, "");
}

/**
 * Assign a value to a variable if it exists, otherwise assign a default value.
 */
function assignValueIfExists<T>(value: T | undefined, defaultValue: T): T {
  return value !== undefined ? value : defaultValue;
}

/**
 * Update the store with the processing message.
 */
function updateStore(processing: boolean, displayIcon: string, displayText: string) {
  store.commit(STORE_MUTATION.SetShowProcessingMessage, processing);
  store.commit(STORE_MUTATION.SetProcessingDisplayIcon, displayIcon);
  store.commit(STORE_MUTATION.SetProcessingDisplayText, displayText);
}

// Store the timeout id for the processing indicator.
let processingTimeoutId: ReturnType<typeof setTimeout> | undefined;

/**
 * SD-3381: Show the processing message on the screen.
 * @param message The message to show.
 */
export const onProcessingFromServer = async function (message: MessageContent) {
  try {
    // If the message is not a typing indicator, ignore it.
    if (!message.processingIndicator) {
      return;
    }

    // If the message is a processing indicator, clear the timeout id.
    if (processingTimeoutId) {
      clearTimeout(processingTimeoutId);
    }

    store.commit(STORE_MUTATION.SetTypingType, getMessageType(message.recordInfo?.senderType));

    let displayIcon: string = '';
    let displayText: string = '';

    // Get the display icon and text from the message
    displayIcon = assignValueIfExists(message.processingIndicator.displayIcon, "");
    displayText = assignValueIfExists(message.processingIndicator.displayText, "");

    // Additional checks for empty, null, undefined, or whitespace values
    if (displayIcon && !displayIcon.trim()) {
      displayIcon = "";
    }
    if (displayText && !displayText.trim()) {
      displayText = "";
    }

    // Both the text and the icon should be optional.
    // However if both are missing, you should ignore the message and display nothing.
    if (!displayText && !displayIcon) {
      updateStore(false, "", "");
      clearTrafficStatusBar();
      return;
    }

    // If there is only text (no icon), show it in the status bar above the input
    // instead of the chat-bubble processing indicator.
    if (displayText && !displayIcon) {
      const timeout = (message.processingIndicator as any).timeout;
      const barTimeout = (timeout !== undefined && timeout !== null) ? timeout : 1000;
      showTrafficStatusBar(displayText.trim(), barTimeout);
      return;
    }

    // SD-3381: Get the latest message timestamp from the storage.
    const lastMsgTimestamp = TimestampsUtil.getLastMessageTimestamp(store.state.pageId);
    const messageTimestamp = TimestampsUtil.getCurrentMessageTimestamp(message);

    
    console.log("Last Message Time : " + lastMsgTimestamp)
    console.log("Processing Message Time : " + messageTimestamp)
    
    // If the message has no timestamp (0), skip the stale-check — always show it.
    // If the message timestamp is less than or equal to the latest timestamp, ignore the message.
    if (messageTimestamp !== 0 && messageTimestamp <= lastMsgTimestamp) {
      updateStore(false, "", "");
      return;
    }

    // Update the store with the processing message.
    updateStore(true, displayIcon.trim(), displayText.trim());

    // Save the current message timestamp to the storage.
    //TimestampsUtil.saveMessageTimestamp(store.state.pageId, messageTimestamp);


    let typingTimeout: number;
    const processingIndicatorTimeout = (message.processingIndicator as any).timeout;

    // If the timeout is specified, use it.
    if (processingIndicatorTimeout !== undefined && processingIndicatorTimeout !== null) {
      typingTimeout = processingIndicatorTimeout;
    } else {
      // Otherwise, use the default timeout.
      typingTimeout = 1000;
    }

    // If the timeout is 0, then the processing message should be displayed indefinitely.
    if (typingTimeout == 0) {
      return;
    }

    // Otherwise, set a timeout to hide the processing message.
    processingTimeoutId = setTimeout(() => {
      updateStore(false, "", "");
    }, typingTimeout);
  } catch (ex) {
    updateStore(false, "", "");
  }
};

/**
 * Clears the processing timeout if it exists and hides the processing message immediately.
 */
export function clearProcessingTimeout(): void {
  try {
    if (!processingTimeoutId) return;
    
    // If showProcessingMessage is true, then skip clearing the timeout 
    // to allow the processing message to be displayed for the intended duration.
    if (store.state.showProcessingMessage) return;    
    clearTimeout(processingTimeoutId);
    processingTimeoutId = undefined;
  } catch (ex) {
    //console.error("[PROCESS] stop timer failed", ex);
  }
}
